import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Toast from '@showclix-shared/Toast';
import {
    ListingAppContext,
    Header,
    Footer,
    useTranslationService,
    translationService
} from '@showclix-shared/ListingComponents';
import ListingView from "./views/ListingView";
import { datesService } from "./services/DatesService";

export const AppContext = ListingAppContext;

const App = (props) => {
    const [loadingListingId, setLoadingListingId] = useState(null);
    const [apiUrl, setApiUrl] = useState(props.configs.apiUrl);
    const [sellerShortName, setSellerShortName] = useState(props.sellerInfo.shortName);
    const [customTranslations, setCustomTranslations] = useState(props.configs.translations);
    const [customLocale, setCustomLocale] = useState(props.configs.jsLocale);

    // Helper to re-instantiate t with new translations
    const getTranslationServiceWithTranslations = (translations) => {
        return useTranslationService(translations || props.configs.translations);
    };

    // Use either custom translations or default
    const { t, isValidLocale } = getTranslationServiceWithTranslations(customTranslations);

    useEffect(() => {
      datesService().useLocale(customLocale);
    }, [customLocale])

    // Locale change listener and translation updater
    useEffect(() => {
      const { getTranslationVersion, getTranslations } = translationService(apiUrl);
      const updateTranslation = async (serverLocale, jsLocale) => {
        try {
          const { version } = await getTranslationVersion(serverLocale, 'org_listing');
          const { locale_strings } = await getTranslations(serverLocale, 'org_listing', version);
          setCustomLocale(jsLocale);
          setCustomTranslations(locale_strings || {});
        } catch (error) {
          console.error(error);
        }
      };
      const handler = async (e) => {
        if (e && e.detail) {
          await updateTranslation(e.detail.serverLocale, e.detail.jsLocale);
        }
      };
      window.addEventListener('localeChanged', handler);
      return () => {
        window.removeEventListener('localeChanged', handler);
      };
    }, [apiUrl]);

    const appContextData = () => ({
        apiUrl,
        setApiUrl,
        sellerShortName,
        setSellerShortName,
        t,
        locale: isValidLocale(customLocale) ? customLocale : 'en-US',
        serverLocale: props.configs.locale,
        serverTimezoneName: props.configs.serverTimezoneName,
        embeddedCheckout: props.embeddedCheckout,
        loadingListingId,
        setLoadingListingId,
        setCustomTranslations,
        setCustomLocale
    });

    // find the requested listing document in the initial result set
    // if a listing was requested in the url for cart mode
    const requestedListingDocument = useMemo(() => {
        return props.embeddedCheckout && props.requestedListingId
            ? props.listings?.find(doc => doc.listing_id == props.requestedListingId)
            : null;
    });

    return (
        <AppContext.Provider value={ appContextData() }>
            <Header logo={props.sellerInfo.partnerLogo} />
            <ListingView
                listings={props.listings ?? []}
                userPhoto={props.sellerInfo.userImage}
                organizationInfo={props.sellerInfo.organizationInfo}
                socialMedia={props.sellerInfo.social}
                backgroundImage={props.sellerInfo.backgroundImage}
                description={props.sellerInfo.description}
                requestedListingDocument={requestedListingDocument}
            />
            <Footer
                logo={props.sellerInfo.partnerLogo}
                copyrightName={props.sellerInfo.copyrightName ?? ''}
                contactUrl={props.sellerInfo?.contact_url ?? ''}
                tosUrl={props.sellerInfo?.tos_url ?? ''}
                privacyUrl={props.sellerInfo?.privacy_url ?? ''}
                doNotSellUrl={props.sellerInfo?.do_not_sell_url ?? ''}
            />
            <Toast />
        </AppContext.Provider>
    );
}

App.propTypes = {
    sellerInfo: PropTypes.object.isRequired,
    listings: PropTypes.array,
    configs: PropTypes.object.isRequired,
    embeddedCheckout: PropTypes.object,
    requestedListingId: PropTypes.number,
}

export default App;
